import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import {
  duplicateConfidenceChipColor,
  formatDuplicateConfidenceLabel,
  formatMatchByLabels,
} from "./clientHygieneDisplay";

function defaultClusterState(cluster) {
  const ids = cluster.memberIds || [];
  return {
    primaryId: cluster.defaultPrimaryId ?? ids[0],
    selectedIds: new Set(ids),
  };
}

export default function HygieneDuplicateClusters({ clusters, t, onMergeCluster, onOpenClient }) {
  const [clusterState, setClusterState] = useState({});

  useEffect(() => {
    const next = {};
    for (const cluster of clusters || []) {
      next[cluster.clusterId] = defaultClusterState(cluster);
    }
    setClusterState(next);
  }, [clusters]);

  const updateCluster = (clusterId, patch) => {
    setClusterState((prev) => ({
      ...prev,
      [clusterId]: { ...prev[clusterId], ...patch },
    }));
  };

  const toggleMember = (cluster, memberId) => {
    const state = clusterState[cluster.clusterId];
    if (!state) return;
    const next = new Set(state.selectedIds);
    if (next.has(memberId)) {
      if (next.size <= 2) return;
      next.delete(memberId);
      if (Number(state.primaryId) === Number(memberId)) {
        const fallback = [...next].sort((a, b) => a - b)[0];
        updateCluster(cluster.clusterId, { selectedIds: next, primaryId: fallback });
        return;
      }
    } else {
      next.add(memberId);
    }
    updateCluster(cluster.clusterId, { selectedIds: next });
  };

  const handleMerge = (cluster) => {
    const state = clusterState[cluster.clusterId];
    if (!state) return;
    const selectedMembers = cluster.members.filter((m) => state.selectedIds.has(m.id));
    if (selectedMembers.length < 2) return;
    const primaryId = Number(state.primaryId);
    const secondaries = selectedMembers.filter((m) => Number(m.id) !== primaryId);
    onMergeCluster?.(primaryId, secondaries, selectedMembers);
  };

  if (!clusters?.length) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" py={3}>
        {t("clientHygiene.noIssues")}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t("clientHygiene.duplicateClusterHint")}
      </Typography>

      {clusters.map((cluster) => {
        const state = clusterState[cluster.clusterId] || defaultClusterState(cluster);
        const selectedCount = cluster.members.filter((m) => state.selectedIds.has(m.id)).length;
        const canMerge = selectedCount >= 2 && state.primaryId != null;

        return (
          <Paper key={cluster.clusterId} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle1" fontWeight={700}>
                      {cluster.label}
                    </Typography>
                    <Chip
                      size="small"
                      label={t("clientHygiene.duplicateClusterCount", {
                        count: cluster.members.length,
                      })}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      color={duplicateConfidenceChipColor(cluster.confidence)}
                      label={formatDuplicateConfidenceLabel(cluster.confidence, t)}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {formatMatchByLabels(cluster.matchedBy, t)}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!canMerge}
                  onClick={() => handleMerge(cluster)}
                  sx={{ flexShrink: 0 }}
                >
                  {t("clientHygiene.mergeCluster", { count: selectedCount })}
                </Button>
              </Stack>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t("clientHygiene.duplicatePrimaryLabel")}
                </Typography>
                <RadioGroup
                  row
                  value={String(state.primaryId ?? "")}
                  onChange={(e) => updateCluster(cluster.clusterId, { primaryId: Number(e.target.value) })}
                >
                  {cluster.members.map((member) => (
                    <FormControlLabel
                      key={member.id}
                      value={String(member.id)}
                      control={<Radio size="small" />}
                      disabled={!state.selectedIds.has(member.id)}
                      label={
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenClient?.(member.id);
                          }}
                          color="primary.main"
                          fontWeight={600}
                        >
                          #{member.id}
                          {member.nome ? ` · ${member.nome}` : ""}
                        </Typography>
                      }
                    />
                  ))}
                </RadioGroup>
              </Box>

              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2">{t("clientHygiene.duplicateIncludeLabel")}</Typography>
                  <Button
                    size="small"
                    onClick={() =>
                      updateCluster(cluster.clusterId, {
                        selectedIds: new Set(cluster.memberIds),
                      })
                    }
                  >
                    {t("clientHygiene.selectAllMembers")}
                  </Button>
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {cluster.members.map((member) => {
                    const checked = state.selectedIds.has(member.id);
                    const isPrimary = Number(state.primaryId) === Number(member.id);
                    return (
                      <FormControlLabel
                        key={member.id}
                        sx={{
                          border: 1,
                          borderColor: isPrimary ? "primary.main" : "divider",
                          borderRadius: 1,
                          px: 1,
                          mr: 0,
                          bgcolor: isPrimary ? "action.hover" : "transparent",
                        }}
                        control={
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={() => toggleMember(cluster, member.id)}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            #{member.id}
                            {isPrimary ? ` (${t("clientHygiene.primaryBadge")})` : ""}
                          </Typography>
                        }
                      />
                    );
                  })}
                </Stack>
              </Box>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
